<script setup lang="ts">
import ForeignModelColumn from '@/components/ForeignModelColumn.vue';
import LiveTrackLyricsInspectorContent from '@/components/LiveTrackLyricsInspectorContent.vue';
import { buttonVariants } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLazyOverflowTooltip } from '@/composables/useLazyOverflowTooltip';
import { useInspectorStore } from '@/stores/inspectorStore';
import { resolveLiveTrackId, liveTrackNameFromRow, liveTrackArtistNameFromRow } from '@/utils/liveTrackRow';
import { cn } from '@/utils/utils';
import { computed, markRaw } from 'vue';

const props = defineProps<{
  field: string;
  dataType: string;
  data: Record<string, unknown>;
}>();

const inspectorStore = useInspectorStore();

function inspectorTitle(data: Record<string, unknown>): string {
  const track = liveTrackNameFromRow(data)?.trim();
  const artist = liveTrackArtistNameFromRow(data)?.trim();
  if (track && artist) {
    return `Lyrics — ${artist} — ${track}`;
  }
  if (track) {
    return `Lyrics — ${track}`;
  }
  return 'Lyrics';
}

const liveTrackIdForInspector = computed(() => resolveLiveTrackId(props.data));

const displayValue = computed(() => {
  const parts = props.field.split('.');
  const attributes = parts.reduce((acc: unknown, key: string) => {
    if (Array.isArray(acc)) {
      return acc.map((item) => (item as Record<string, unknown> | undefined)?.[key]);
    }
    return (acc as Record<string, unknown> | undefined)?.[key];
  }, props.data as unknown);

  if (!attributes) {
    return '';
  }
  if (Array.isArray(attributes)) {
    return attributes.join(', ');
  }
  if (typeof attributes === 'object' && !Array.isArray(attributes)) {
    const objectValue = attributes as Record<string, unknown>;
    if (typeof objectValue.display_string === 'string') {
      return objectValue.display_string;
    }
    if (typeof objectValue.name === 'string') {
      return objectValue.name;
    }
  }
  return String(attributes);
});

// The clickable cell opens the lyrics inspector; the lazy overflow tooltip (shared composable,
// since the trigger is a <button>) surfaces the full value only when it overflows and is hovered.
const {
  activated,
  probeRef,
  triggerRootRef,
  onProbePointerOver,
  onProbePointerOut,
  onProbeFocusIn,
  onProbeFocusOut,
  textRef,
  tooltipOpen,
  onOpenChange,
} = useLazyOverflowTooltip(displayValue);
const buttonClass = cn(
  buttonVariants({ variant: 'ghost' }),
  'h-auto min-h-0 w-full min-w-0 shrink px-1 py-1 text-left font-normal text-sm underline-offset-2 hover:underline',
);

function onOpenTrackLyricsInspector(): void {
  const liveTrackId = liveTrackIdForInspector.value;
  if (liveTrackId == null) {
    return;
  }
  inspectorStore.open(inspectorTitle(props.data), markRaw(LiveTrackLyricsInspectorContent), {
    liveTrackId,
  });
}
</script>

<template>
  <div class="min-w-0 w-full max-w-full">
    <template v-if="liveTrackIdForInspector != null">
      <button
        v-if="!activated"
        type="button"
        :class="buttonClass"
        @click="onOpenTrackLyricsInspector"
        @pointerover="onProbePointerOver"
        @pointerout="onProbePointerOut"
        @focusin="onProbeFocusIn"
        @focusout="onProbeFocusOut"
      >
        <span ref="probeRef" tabindex="-1" class="block w-full min-w-0 max-w-full truncate">
          {{ displayValue }}
        </span>
      </button>
      <Tooltip v-else :open="tooltipOpen" @update:open="onOpenChange">
        <TooltipTrigger as-child>
          <button ref="triggerRootRef" type="button" :class="buttonClass" @click="onOpenTrackLyricsInspector">
            <span
              ref="textRef"
              tabindex="-1"
              class="block w-full min-w-0 max-w-full truncate"
            >
              {{ displayValue }}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          class="max-w-xs break-words pointer-events-none"
          :collision-padding="16"
          side="top"
          align="start"
        >
          <p class="pointer-events-none">{{ displayValue }}</p>
        </TooltipContent>
      </Tooltip>
    </template>
    <ForeignModelColumn
      v-else
      :field="field"
      :data-type="dataType"
      :data="data"
    />
  </div>
</template>
